/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {CustomMouseEvent, Events, throttleQueue, UserKeyEvent} from '@exc/shared';
import {generateShellUrl} from './util';
import type {RuntimeConfiguration} from '@adobe/exc-app';
import type {RuntimeMessenger} from './models/runtimeModels';

export interface Actions {
  onClick: (event: CustomMouseEvent, config: RuntimeConfiguration, allowedHosts: string[]) => void;
  onContextMenu: (event: MouseEvent, config: RuntimeConfiguration, allowedHosts: string[]) => void;
  onKeyDown: (event: UserKeyEvent) => void;
  onMouseDown: (event: CustomMouseEvent, config: RuntimeConfiguration, allowedHosts: string[]) => void;
  reportActivity: () => void;
}

/**
 * Reset a link or anchor URL that has been modified by the
 * context menu.
 * @param target The link or anchor targeted by context menu event.
 * @param href The original url of the target link or anchor.
 */
function setupUrlReset(target: HTMLElement, href: string): void {
  const events = ['click', 'keydown', 'mouseleave'];
  function setHref(event: Event): void {
    const t = event.target as HTMLAnchorElement;
    t.href = href;
    events.forEach(key => t.removeEventListener(key, setHref));
  }
  events.forEach(key => target.addEventListener(key, setHref));
}

/**
 * Finds and validates the true anchor element of a mouse event. Returns
 * undefined if the anchor is meant to be ignored (data-unified-shell-ignore).
 * @param {CustomMouseEvent} event Mouse event to process
 * @returns {HTMLAnchorElement=} The target anchor or undefined if
 *   data-unified-shell-ignore is true
 */
function getTarget(event: CustomMouseEvent): HTMLAnchorElement | undefined {
  let target = event.target as HTMLAnchorElement;
  target = target.closest('a') || target;
  return target.getAttribute('data-unified-shell-ignore') !== 'true' ? target : undefined;
}

const IGNORED_KEY_SELECTORS = [
  'div.ql-editor', // Analytics WYSIWYG editor
  'input',
  'textarea',
  'div[contenteditable="true"]'
];

export default (messenger: RuntimeMessenger, window: Window): Actions => {
  const {document} = window;
  const reportActivity = throttleQueue(() => messenger.send(Events.ACTIVE, {}), 1000, true, window);

  /**
   * Handles keydown events (escape in particular) to send focus back up to the parent.
   * @param {UserKeyEvent} event A keyboard event.
   */
  const onKeyDown = (event: UserKeyEvent): void => {
    // If user presses escape key, simulate click on iframe
    reportActivity();
    if (event.keyCode === 27) {
      messenger.send(Events.FOCUS, {});
    }
    // Send keyboard events to shell unless they came from shell or occurred in input or text areas.
    const element = document.activeElement;
    if (!event.fromShell && element && !IGNORED_KEY_SELECTORS.some(selector => element && element.matches(selector))) {
      messenger.send(Events.KEYPRESS, {
        altKey: event.altKey,
        bubbles: event.bubbles,
        ctrlKey: event.ctrlKey,
        isComposing: event.isComposing,
        key: event.key,
        keyCode: event.keyCode,
        location: event.location,
        metaKey: event.metaKey,
        repeat: event.repeat,
        shiftKey: event.shiftKey,
        type: event.type
      });
    }
  };

  /**
   * Handles the context menu event to ensure "Open in New Tab/Window" is correct.
   * @param {MouseEvent} event The mouse event from the contextmenu.
   * @param {RuntimeConfiguration} config The external configuration.
   * @param {string[]} allowedHosts Allowed hosts to target
   */
  const onContextMenu = (event: MouseEvent, config: RuntimeConfiguration, allowedHosts: string[]): void => {
    const target = getTarget(event);
    if (!target) {
      return;
    }

    if (allowedHosts.includes(target.host)) {
      const originalHref = target.href;
      setupUrlReset(target, originalHref);
      target.href = generateShellUrl(config, {href: originalHref});
    }
  };

  const updateShellLinks = (config: RuntimeConfiguration, target: HTMLAnchorElement): void => {
    const targetAttribute = target.getAttribute('target') || '';
    const browserUrl = new URL(config.baseUrl);
    // Handle Unified Shell links to avoid a full page refresh
    if (target.host === browserUrl.host && ['_parent', '_top'].indexOf(targetAttribute) > -1) {
      const targetUrl = new URL(target.href);
      // if the path isn't '/', we need to construct the url to navigate to
      if (targetUrl.pathname.length > 1) {
        browserUrl.hash = `#${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
        target.setAttribute('href', browserUrl.toString());
      }
      // Otherwise, ensure that the query params are maintained
      if (targetUrl.pathname === '/' && browserUrl.search) {
        targetUrl.search = browserUrl.search;
        target.setAttribute('href', targetUrl.toString());
      }
    }
  };

  /**
   * Handle command click and regular click.
   * @param {CustomMouseEvent} event The click event being handled.
   * @param {RuntimeConfiguration} config The external configuration.
   * @param {string[]} allowedHosts Allowed hosts to target
   */
  const onClick = (event: CustomMouseEvent, config: RuntimeConfiguration, allowedHosts: string[]): void => {
    // Event came from the Shell via user input. Don't let this propagate back
    // to the Shell.
    if (event.fromShell) {
      return;
    }

    reportActivity();
    const target = getTarget(event);
    if (!target) {
      return;
    }

    // Hack for Target's footer
    const isTargetFooter = config.appId === 'target' && target.nodeName === 'A' && (event.target as HTMLAnchorElement).closest('.endor-Footer');

    // Hack for ACS per https://jira.corp.adobe.com/browse/EXC-21834
    const isACSLink = config.appId === 'acs' && target.nodeName === 'A' && target.className === 'so-LinkView__anchor' &&
      target.target === '_blank' && /^https:\/\/(.*)\/rest\/head(.*)\.json/.test((target).href);

    if (isTargetFooter || isACSLink) {
      event.preventDefault();
      window.open(target.href);
      messenger.send(Events.CLICK, {});
      return;
    }

    // Handle Unified Shell links to avoid a full page refresh
    updateShellLinks(config, target);

    // Window location and link location are the same, so we need to make sure
    // it's wrapped in a Shell.
    if (allowedHosts.includes(target.host)) {
      // The new tab key is different based on platform (OSX uses command while
      // windows uses control)
      const newTabKey = /[Mm]ac/.test(window.navigator.platform) ? event.metaKey : event.ctrlKey;
      // Either the new tab key was used or there is a target="_blank" attribute
      // on the anchor. We need to open this in a new window within the Shell.
      // Block the default action since we don't want to open the
      // link in a new window without the Shell.
      if (newTabKey || target.getAttribute('target') === '_blank') {
        event.preventDefault();
        window.open(generateShellUrl(config, {href: target.href}));
        messenger.send(Events.CLICK, {});
      }
      return;
    }

    // Check if href already has target attribute so we overwrite _blank
    if (target.href && !target.hasAttribute('target')) {
      // open the link in the parent window
      target.setAttribute('target', '_parent');
    }
    messenger.send(Events.CLICK, {});
  };

  /**
   * Handle middle mouse down button.
   */
  const onMouseDown = (event: CustomMouseEvent, config: RuntimeConfiguration, allowedHosts: string[]): void => {
    reportActivity();
    const target = getTarget(event);
    if (!target) {
      return;
    }
    // We only care about middle mouse button events here. onClick handles others.
    if (event.button !== 1 || !allowedHosts.includes(target.host)) {
      return;
    }
    window.open(generateShellUrl(config, {href: target.href}));
  };

  return {
    onClick,
    onContextMenu,
    onKeyDown,
    onMouseDown,
    reportActivity
  };
};
